import { useMemo } from "react"
import { useAppSelector } from "@/reducers/hooks"
import {
  selectActiveChat,
  selectDeliveryOrder,
  selectHasRouteUnchanged,
  selectRouteUpdates,
} from "@/reducers/chatsSliceSelectors"
import { selectChatMessages } from "@/reducers/messagesSliceSelectors"
import { type Message } from "@/reducers/messagesSlice"
import { useDocumentLink } from "@/hooks/useDocumentLink"

export const useShowAITouchedBadge = (message: Message) => {
  const activeChat = useAppSelector(selectActiveChat)
  const chatJid = activeChat.jid
  const chatWithMessages = useAppSelector(selectChatMessages(chatJid))

  const routeUpdates = selectRouteUpdates(chatWithMessages, message?.id)
  const hasRouteUnchanged = selectHasRouteUnchanged(
    chatWithMessages,
    message?.id,
  )

  const deliveryOrder = selectDeliveryOrder(chatWithMessages, message.id)
  const { documentLinks, linkedFiles, linkType } = useDocumentLink(message)

  const translation = useMemo(
    () => Boolean(message.translation),
    [message.translation],
  )

  const transcription = useMemo(() => {
    return Boolean(
      message.fileName &&
        /\.oga|\.mp3|\.wav|\.m4a/i.test(message.fileName) &&
        message.body.indexOf(message.fileName) === -1,
    )
  }, [message.fileName, message.body])

  return useMemo(() => {
    return {
      showAITouchedBadge: Boolean(
        routeUpdates ||
          hasRouteUnchanged ||
          transcription ||
          translation ||
          deliveryOrder ||
          linkedFiles.length > 0,
      ),
      routeUpdates,
      hasRouteUnchanged,
      transcription,
      translation,
      deliveryOrder,
      documentLinks,
      linkedFiles,
      linkType,
    }
  }, [
    routeUpdates,
    hasRouteUnchanged,
    transcription,
    translation,
    deliveryOrder,
    documentLinks,
    linkedFiles,
    linkType,
  ])
}
